/**
 * CheckingProductionEntry · P17-UAT-PRODUCTION-UI-01 REV-C
 * Owner-approved visual composition: Apple-grade simplicity + SAP Fiori
 * enterprise structure + MES industrial control.
 *
 * UI / UX PRESENTATION refinement ONLY. Business logic, Stock Engine V2,
 * posting authority and endpoints are UNCHANGED from REV-A / REV-B.
 * Defect Details and Shift are PRESENTATION-ONLY local state (the current data
 * model has no persisted field for them — nothing is written to the backend).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import {
  TransactionSection, FormField, NumericField, ReferenceSelect, ReadOnlyField,
  TransactionActionBar, txnInputCls, txnTextareaCls, txnBtn, txnBtnSize,
} from "@/components/txn";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  CaretRightIcon, FloppyDiskIcon, CheckCircleIcon, ArrowLeftIcon, XIcon,
  PlusIcon, TrashIcon,
} from "@phosphor-icons/react";

const TID = "checking-entry";
const REGISTER_ROUTE = "/reports/checking-production-register";
const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v) || 0);
const fmt = (v, d = 3) =>
  v === null || v === undefined || v === "" || isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

const SHIFTS = ["Day", "Night", "General"];

// Compact quality-distribution doughnut — Checking-specific, SVG only (REV-C §13).
function QualityDonut({ first, second, waste }) {
  const total = first + second + waste;
  const R = 42, C = 2 * Math.PI * R;
  const segs = [
    { v: first, c: "#34d399" },   // First Quality · emerald
    { v: second, c: "#fbbf24" },  // Seconds · amber
    { v: waste, c: "#fb7185" },   // Waste · coral (NOT error red)
  ];
  let offset = 0;
  return (
    <svg viewBox="0 0 100 100" className="w-24 h-24" data-testid={`${TID}-donut`}>
      <circle cx="50" cy="50" r={R} fill="none" stroke="var(--border)" strokeWidth="11" />
      {total > 0 && segs.map((s, i) => {
        const len = (s.v / total) * C;
        const el = (
          <circle key={i} cx="50" cy="50" r={R} fill="none" stroke={s.c} strokeWidth="11"
            strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset}
            transform="rotate(-90 50 50)" />
        );
        offset += len;
        return el;
      })}
      <text x="50" y="48" textAnchor="middle" className="fill-[var(--fg-0)]" style={{ fontSize: "12px", fontWeight: 800, fontFamily: "monospace" }}>
        {total > 0 ? fmt(total, 1) : "0.0"}
      </text>
      <text x="50" y="60" textAnchor="middle" className="fill-[var(--fg-3)]" style={{ fontSize: "7px", letterSpacing: "0.1em" }}>
        KG TOTAL
      </text>
    </svg>
  );
}

export default function CheckingProductionEntry() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [searchParams] = useSearchParams();
  const wantReview = searchParams.get("review") === "1";
  const canEdit = hasPerm ? hasPerm("inventory:edit") : true;

  const [wos, setWos] = useState([]);
  const [woId, setWoId] = useState("");
  const [itemId, setItemId] = useState("");
  const [wantItem, setWantItem] = useState(null);   // draft-edit: item to restore
  const [wantSrc, setWantSrc] = useState(null);      // draft-edit: source batch to restore
  const [loadedEdit, setLoadedEdit] = useState(false);
  const [sources, setSources] = useState([]);
  const [srcBatch, setSrcBatch] = useState("");
  const [total, setTotal] = useState("");
  const [first, setFirst] = useState("");
  const [second, setSecond] = useState("");
  const [waste, setWaste] = useState("");
  const [remarks, setRemarks] = useState("");
  const [shift, setShift] = useState("Day");           // presentation only
  const [defects, setDefects] = useState([]);           // presentation only (not persisted)
  const [draftId, setDraftId] = useState(null);
  const [draftNo, setDraftNo] = useState(null);
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gpm, setGpm] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  useEffect(() => { api.get("/work-orders/").then(r => setWos(r.data || [])).catch(() => setWos([])); }, []);

  // Draft continuation (P17-UAT-17B): load an existing CPPE draft by id for Edit / Review & Post.
  useEffect(() => {
    if (!editId || loadedEdit || wos.length === 0) return;
    (async () => {
      try {
        const rows = await api.get("/checking-production-entries").then(r => r.data || []);
        const row = rows.find(x => x.id === editId);
        if (!row) { toast.error("Draft not found"); return; }
        setWoId(row.work_order_id || "");
        setWantItem(row.item_id || null);
        setWantSrc(row.source_batch || null);
        setTotal(row.total_checked_kg == null ? "" : String(row.total_checked_kg));
        setFirst(row.first_quality_kg == null ? "" : String(row.first_quality_kg));
        setSecond(row.second_quality_kg == null ? "" : String(row.second_quality_kg));
        setWaste(row.wastage_kg == null ? "" : String(row.wastage_kg));
        setRemarks(row.remarks || "");
        if (row.bom_standard_gpm != null) setGpm(Number(row.bom_standard_gpm));
        setDraftId(row.id);
        setDraftNo(row.entry_no);
        const p = await api.get(`/production-entries/${row.id}/checking-preview`).then(r => r.data).catch(() => null);
        if (p) setPv(p);
        setLoadedEdit(true);
      } catch { toast.error("Failed to load draft"); }
    })();
  }, [editId, wos, loadedEdit]);

  const wo = useMemo(() => wos.find(w => w.id === woId), [wos, woId]);
  const items = useMemo(() => {
    if (!wo) return [];
    const li = wo.items && wo.items.length ? wo.items : [{ item_id: wo.item_id, product_code: wo.item_code || wo.product_code }];
    return li.filter(x => x.item_id);
  }, [wo]);
  const selItem = useMemo(() => items.find(x => x.item_id === itemId), [items, itemId]);
  const product = selItem ? (selItem.product_code || selItem.item_code) : "";
  const soEso = wo ? (wo.source_order_no || "") : "";
  const customer = wo ? (wo.customer_name || "") : "";
  const productDesc = (selItem && selItem.item_description) || (wo && wo.item_description) || "";
  const style = selItem ? (selItem.style_no || "") : "";
  const uom = (selItem && selItem.unit) || (product ? "KG" : "");
  const selSource = useMemo(() => sources.find(s => s.batch_lot === srcBatch), [sources, srcBatch]);
  const availKg = selSource ? Number(selSource.available_kg) : 0;

  useEffect(() => {
    if (wantItem && items.some(x => x.item_id === wantItem)) { setItemId(wantItem); return; }
    if (items.length === 1) setItemId(items[0].item_id);
  }, [items, wantItem]);

  useEffect(() => {
    setSources([]); setSrcBatch("");
    if (!woId || !product) return;
    api.get(`/checking/eligible-sources?wo_id=${encodeURIComponent(woId)}&product_code=${encodeURIComponent(product)}`)
      .then(r => {
        const s = (r.data && r.data.sources) || [];
        setSources(s);
        setGpm(r.data && r.data.bom_standard_gpm != null ? Number(r.data.bom_standard_gpm) : null);
        if (wantSrc && s.some(x => x.batch_lot === wantSrc)) setSrcBatch(wantSrc);
        else if (s.length === 1) setSrcBatch(s[0].batch_lot);
      })
      .catch(() => setSources([]));
  }, [woId, product, wantSrc]);

  // Deep-link "Review & Post" from the Register opens the canonical review dialog once postable.
  useEffect(() => {
    if (wantReview && loadedEdit && pv && pv.postable) setReviewOpen(true);
  }, [wantReview, loadedEdit, pv]);

  const totalN = num(total), firstN = num(first), secondN = num(second), wasteN = num(waste);
  const sum = firstN + secondN + wasteN;
  const conserved = totalN > 0 && Math.abs(totalN - sum) < 0.001;
  const pct = (x) => (totalN > 0 ? ((x / totalN) * 100).toFixed(2) : "0.00");
  const stockOk = totalN > 0 && totalN <= availKg + 1e-6;
  const remaining = Math.max(availKg - totalN, 0);
  const mOf = (kg) => (gpm && gpm > 0 ? (Number(kg || 0) * 1000 / gpm) : null);
  const fmM = (kg) => { const m = mOf(kg); return m == null ? "—" : fmt(m, 3); };  // REV-A · metres number only (separate column)
  const r_no = (p) => (p && p.header && p.header.entry_no) || "";

  const saveDraft = async () => {
    if (!woId || !itemId) return toast.error("Select a Work Order and item");
    if (!srcBatch) return toast.error("Select a PACK-WIP source batch");
    setBusy(true);
    try {
      const body = {
        work_order_id: woId, item_id: itemId, product_code: product,
        source_godown_id: selSource?.godown_id, source_batch_lot: srcBatch,
        total_checked_kg: totalN, first_quality_kg: firstN, second_quality_kg: secondN,
        wastage_kg: wasteN, remarks, entry_date: new Date().toISOString().slice(0, 10),
      };
      let id = draftId;
      if (!id) {
        const r = await api.post("/checking-production-entries", body);
        id = r.data.id; setDraftId(id); setDraftNo(r.data.entry_no);
      } else {
        await api.put(`/checking-production-entries/${id}`, body);
      }
      const p = await api.get(`/production-entries/${id}/checking-preview`).then(r => r.data).catch(e => ({ ok: false, reasons: [e?.response?.data?.detail || "preview error"] }));
      setPv(p);
      toast.success(`Draft saved · ${draftNo || r_no(p)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const postChecking = async () => {
    if (!draftId) return toast.error("Save the draft first");
    setBusy(true);
    try {
      const r = await api.post(`/production-entries/${draftId}/checking-post`, {});
      toast.success(`Checking posted · First ${r.data?.first_kg} kg → ${r.data?.destination_godown} · batch ${r.data?.first_output_batch}`);
      const p = await api.get(`/production-entries/${draftId}/checking-preview`).then(r => r.data);
      setPv(p);
      setReviewOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally { setBusy(false); }
  };

  const posted = !!(pv && pv.mode === "POSTED");
  const statusLabel = posted ? "Authorised" : "Draft";
  const dispInvalid = totalN > 0 && !conserved;     // mass imbalance = validation error (strong red)
  const totalInvalid = totalN > 0 && !stockOk;      // insufficient stock = validation error (strong red)
  const today = new Date().toISOString().slice(0, 10);
  const canReview = canEdit && !posted && conserved && stockOk;

  const woOptions = useMemo(() => wos.map(w => {
    const li = (w.items && w.items[0]) || {};
    return { value: w.id, code: w.po_no || w.wo_no || "", name: li.product_code || li.item_code || w.item_code || w.product_code || "", meta: w.source_order_no || "" };
  }), [wos]);
  const itemOptions = useMemo(() => items.map(it => ({ value: it.item_id, code: it.product_code || it.item_code || "", name: it.style_no || "" })), [items]);
  const sourceOptions = useMemo(() => sources.map(s => ({ value: s.batch_lot, code: s.batch_lot, meta: `${fmt(s.available_kg, 3)} kg` })), [sources]);

  const addDefect = () => setDefects(d => [...d, { id: Date.now() + Math.random(), type: "", description: "", qty: "", remark: "" }]);
  const setDefect = (id, k, val) => setDefects(d => d.map(x => (x.id === id ? { ...x, [k]: val } : x)));
  const rmDefect = (id) => setDefects(d => d.filter(x => x.id !== id));

  const SummaryRow = ({ label, kg, m, strong, tone }) => (
    <div className={`grid grid-cols-[1.4fr_1fr_1fr] items-center gap-2 px-3 ${strong ? "py-2.5" : "py-1.5"} ${strong ? "" : "border-b border-[var(--border)]"}`}>
      <span className={`text-xs ${strong ? "font-semibold text-[var(--fg-0)]" : "text-[var(--fg-2)]"}`}>{label}</span>
      <span className={`font-mono text-right ${strong ? "text-base font-black" : "text-sm"} ${tone || "text-[var(--fg-0)]"}`}>{kg}</span>
      <span className={`font-mono text-right ${strong ? "text-base font-black" : "text-sm"} ${tone || "text-[var(--fg-0)]"}`}>{m}</span>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto pb-24" data-testid={`${TID}-page`}>
      {/* ── Page Header (REV-C §4) ─────────────────────────────── */}
      <header className="mb-5" data-testid={`${TID}-header`}>
        <nav className="flex items-center gap-1.5 text-[11px] text-[var(--fg-3)] mb-2" data-testid={`${TID}-breadcrumb`}>
          <span>Production</span><CaretRightIcon size={10} weight="bold" />
          <span>Checking</span><CaretRightIcon size={10} weight="bold" />
          <span className="text-[var(--fg-1)]">Production Entry</span>
        </nav>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-[var(--fg-0)]">
              Checking Production Entry
            </h1>
            <p className="text-xs text-[var(--fg-2)] mt-1 max-w-xl">
              Inspect finished/dyed elastic and classify into First Quality, Seconds and Waste.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <StatusBadge status={statusLabel} testId={`${TID}-status`} />
              {!posted && <span className="text-[10px] font-semibold tracking-widest text-[var(--fg-3)] uppercase">Not Posted</span>}
              <button onClick={() => navigate(REGISTER_ROUTE)} data-testid={`${TID}-view-register`}
                className={`${txnBtn.ghost} h-8 text-xs`}>View Register</button>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-[var(--fg-2)]">
              <span className="font-mono text-[var(--fg-1)]" data-testid={`${TID}-docno`}>{draftNo || "CPPE · new"}</span>
              <span className="text-[var(--fg-3)]">·</span>
              <span className="font-mono" data-testid={`${TID}-date`}>{today}</span>
              <span className="text-[var(--fg-3)]">·</span>
              <select value={shift} onChange={e => setShift(e.target.value)} disabled={posted}
                data-testid={`${TID}-shift`}
                className="h-6 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-[11px] text-[var(--fg-0)] focus:outline-none focus:border-[var(--accent)]">
                {SHIFTS.map(s => <option key={s} value={s}>{s} Shift</option>)}
              </select>
            </div>
          </div>
        </div>
      </header>

      <div className="space-y-4">
        {/* 1 · Document & Reference Information */}
        <TransactionSection index={1} title="Document & Reference Information" testId={`${TID}-sec-reference`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField label="Work Order" required testId={`${TID}-wo-field`} className="lg:col-span-2">
              <ReferenceSelect testid={`${TID}-wo`} options={woOptions} value={woId}
                onChange={(v) => { setWoId(v); setItemId(""); setDraftId(null); setDraftNo(null); setPv(null); }}
                placeholder="— Select WO · Product · SO/ESO —" emptyLabel="No work orders match." disabled={posted} />
            </FormField>
            <FormField label="SO / ESO Reference" testId={`${TID}-soeso-field`}>
              <ReadOnlyField testId={`${TID}-soeso`} value={soEso} mono tone="muted" />
            </FormField>
            {items.length > 1 && (
              <FormField label="WO Item" required testId={`${TID}-item-field`} className="lg:col-span-2">
                <ReferenceSelect testid={`${TID}-item`} options={itemOptions} value={itemId}
                  onChange={setItemId} placeholder="— Select Item —" disabled={posted} />
              </FormField>
            )}
            <FormField label="Product Code" testId={`${TID}-product-field`} className={items.length > 1 ? "" : "lg:col-span-2"}>
              <ReadOnlyField testId={`${TID}-product`} value={product} mono tone="accent" />
            </FormField>
            <FormField label="UOM" testId={`${TID}-uom-field`}>
              <ReadOnlyField testId={`${TID}-uom`} value={uom} mono tone="muted" />
            </FormField>
            <FormField label="Product Description" testId={`${TID}-proddesc-field`} className="sm:col-span-2 lg:col-span-2">
              <ReadOnlyField testId={`${TID}-proddesc`} value={productDesc} tone="muted" />
            </FormField>
            <FormField label="Customer" testId={`${TID}-customer-field`}>
              <ReadOnlyField testId={`${TID}-customer`} value={customer} tone="muted" />
            </FormField>
            <FormField label="Style / Shade" testId={`${TID}-style-field`}>
              <ReadOnlyField testId={`${TID}-style`} value={style} mono tone="muted" />
            </FormField>
          </div>
        </TransactionSection>

        {/* 2 · Material & Source Batch */}
        <TransactionSection index={2} title="Material & Source Batch" testId={`${TID}-sec-material`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormField label="Source Godown" testId={`${TID}-godown-field`}>
              <ReadOnlyField testId={`${TID}-godown`} value={selSource ? (selSource.godown_code || "PACK-WIP") : (sources.length ? "PACK-WIP" : "")} mono tone="muted" />
            </FormField>
            <FormField label="Source Batch / Lot" required testId={`${TID}-source-field`}>
              <ReferenceSelect testid={`${TID}-source`} options={sourceOptions} value={srcBatch} onChange={setSrcBatch}
                placeholder={sources.length ? "— Select batch —" : "No eligible PACK-WIP stock"}
                emptyLabel="No eligible PACK-WIP stock." disabled={posted || !sources.length} />
            </FormField>
            <FormField label="BOM Standard Weight" testId={`${TID}-gpm-field`}>
              <ReadOnlyField testId={`${TID}-gpm`} value={gpm ? `${fmt(gpm, 3)} g/m` : "—"} mono tone="sky" title="Authoritative BOM Standard g/m (frozen on post)" />
            </FormField>
          </div>
          {/* REV-A · Available quantity as SEPARATE kg + m read-only fields (Finishing dual-UOM pattern) */}
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormField label="Available (kg)" testId={`${TID}-avail-field`}>
              <ReadOnlyField testId={`${TID}-avail`} value={fmt(availKg, 3)} mono tone="emerald" className="justify-end" title="Available physical weight in PACK-WIP (authoritative)" />
            </FormField>
            <FormField label="Available (m)" testId={`${TID}-avail-m-field`}>
              <ReadOnlyField testId={`${TID}-avail-m`} value={fmM(availKg)} mono tone="emerald" className="justify-end" title="Derived length at BOM Standard g/m (read-only)" />
            </FormField>
          </div>
          {selSource && (
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-[var(--fg-3)] border-t border-[var(--border)] pt-2.5" data-testid={`${TID}-batch-details`}>
              <span>From Document: <span className="font-mono text-[var(--fg-1)]">{selSource.batch_lot}</span></span>
              <span>Process: <span className="text-[var(--fg-1)]">Finishing → PACK-WIP (SFG)</span></span>
              <span>WO: <span className="font-mono text-[var(--fg-1)]">{selSource.wo_no || "—"}</span></span>
            </div>
          )}
        </TransactionSection>

        {/* 3 · Checking Disposition */}
        <TransactionSection index={3} title="Checking Disposition (KG primary · Metres derived)" testId={`${TID}-sec-disposition`}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-4">
              {/* REV-A · KG and METRES as SEPARATE labelled columns (Finishing dual-UOM pattern).
                  KG editable in DRAFT · METRES read-only, system-calculated from BOM Standard g/m. */}
              <div className="space-y-2" data-testid={`${TID}-disposition-grid`}>
                <div className="grid grid-cols-[1.3fr_1fr_1fr_3.2rem] gap-3 items-center px-0.5 overline text-[var(--fg-3)]">
                  <span>Disposition</span>
                  <span className="text-right">Quantity (kg)</span>
                  <span className="text-right">Length (m)</span>
                  <span className="text-right">Share</span>
                </div>
                <div className="grid grid-cols-[1.3fr_1fr_1fr_3.2rem] gap-3 items-center" data-testid={`${TID}-row-total`}>
                  <label className="text-sm text-[var(--fg-1)] flex items-center gap-1">Total Checked <span className="text-red-400">*</span></label>
                  <NumericField data-testid={`${TID}-total`} value={total} onChange={e => setTotal(e.target.value)} invalid={totalInvalid} disabled={posted} />
                  <ReadOnlyField testId={`${TID}-total-m`} value={fmM(totalN)} mono tone="muted" className="justify-end" />
                  <span className="text-[10px] font-mono text-[var(--fg-3)] text-right">100.00%</span>
                </div>
                <div className="grid grid-cols-[1.3fr_1fr_1fr_3.2rem] gap-3 items-center" data-testid={`${TID}-row-first`}>
                  <label className="text-sm text-[var(--fg-1)] flex items-center gap-1">First Quality <span className="text-red-400">*</span></label>
                  <NumericField data-testid={`${TID}-first`} value={first} onChange={e => setFirst(e.target.value)} invalid={dispInvalid} disabled={posted} />
                  <ReadOnlyField testId={`${TID}-first-m`} value={fmM(firstN)} mono tone="muted" className="justify-end" />
                  <span className="text-[10px] font-mono text-emerald-400 text-right" data-testid={`${TID}-first-pct`}>{pct(firstN)}%</span>
                </div>
                <div className="grid grid-cols-[1.3fr_1fr_1fr_3.2rem] gap-3 items-center" data-testid={`${TID}-row-second`}>
                  <label className="text-sm text-[var(--fg-1)]">Seconds</label>
                  <NumericField data-testid={`${TID}-second`} value={second} onChange={e => setSecond(e.target.value)} invalid={dispInvalid} disabled={posted} />
                  <ReadOnlyField testId={`${TID}-second-m`} value={fmM(secondN)} mono tone="muted" className="justify-end" />
                  <span className="text-[10px] font-mono text-amber-400 text-right" data-testid={`${TID}-second-pct`}>{pct(secondN)}%</span>
                </div>
                <div className="grid grid-cols-[1.3fr_1fr_1fr_3.2rem] gap-3 items-center" data-testid={`${TID}-row-waste`}>
                  <label className="text-sm text-[var(--fg-1)]">Waste</label>
                  <NumericField data-testid={`${TID}-waste`} value={waste} onChange={e => setWaste(e.target.value)} invalid={dispInvalid} disabled={posted} />
                  <ReadOnlyField testId={`${TID}-waste-m`} value={fmM(wasteN)} mono tone="muted" className="justify-end" />
                  <span className="text-[10px] font-mono text-rose-400 text-right" data-testid={`${TID}-waste-pct`}>{pct(wasteN)}%</span>
                </div>
              </div>
              {/* Mass balance (REV-C §12) */}
              <div data-testid={`${TID}-conservation`}
                className={`flex items-center gap-2 rounded-sm px-3 py-2 text-xs font-mono border ${
                  totalN <= 0 ? "border-[var(--border)] text-[var(--fg-3)]"
                  : conserved && stockOk ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/40"
                  : "bg-red-500/10 text-red-400 border-red-500/50"}`}>
                <span>{fmt(totalN)} = {fmt(firstN)} + {fmt(secondN)} + {fmt(wasteN)}</span>
                <span className="ml-auto font-sans font-semibold tracking-wide">
                  {totalN <= 0 ? "ENTER QUANTITIES"
                    : !conserved ? "✕ NOT BALANCED"
                    : !stockOk ? `✕ EXCEEDS AVAILABLE ${fmt(availKg)} KG`
                    : "✓ BALANCED"}
                </span>
              </div>
            </div>
            {/* Quality distribution doughnut */}
            <div className="flex lg:flex-col items-center justify-center gap-4 lg:border-l lg:border-[var(--border)] lg:pl-5">
              <QualityDonut first={firstN} second={secondN} waste={wasteN} />
              <div className="space-y-1.5 text-[11px]">
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-400" /><span className="text-[var(--fg-2)]">First</span><span className="font-mono text-[var(--fg-0)] ml-auto">{fmt(firstN)} ({pct(firstN)}%)</span></div>
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400" /><span className="text-[var(--fg-2)]">Seconds</span><span className="font-mono text-[var(--fg-0)] ml-auto">{fmt(secondN)} ({pct(secondN)}%)</span></div>
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm bg-rose-400" /><span className="text-[var(--fg-2)]">Waste</span><span className="font-mono text-[var(--fg-0)] ml-auto">{fmt(wasteN)} ({pct(wasteN)}%)</span></div>
              </div>
            </div>
          </div>
        </TransactionSection>

        {/* 4 · Defect Details */}
        <TransactionSection index={4} title="Defect Details (If any)" testId={`${TID}-sec-defects`}
          right={<button onClick={addDefect} disabled={posted} data-testid={`${TID}-add-defect`} className={`${txnBtn.ghost} h-7 text-xs`}><PlusIcon size={13} weight="bold" /> Add Defect</button>}>
          {defects.length === 0 ? (
            <div className="text-xs text-[var(--fg-3)] py-1">No defects recorded.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid={`${TID}-defect-table`}>
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-[var(--fg-3)] text-left border-b border-[var(--border)]">
                    <th className="py-1.5 pr-2 w-6">#</th>
                    <th className="py-1.5 pr-2">Defect Type</th>
                    <th className="py-1.5 pr-2">Description</th>
                    <th className="py-1.5 pr-2 w-28 text-right">Qty (kg)</th>
                    <th className="py-1.5 pr-2">Remark</th>
                    <th className="py-1.5 w-8" />
                  </tr>
                </thead>
                <tbody>
                  {defects.map((d, i) => (
                    <tr key={d.id} className="border-b border-[var(--border)]/60">
                      <td className="py-1.5 pr-2 font-mono text-[var(--fg-3)]">{i + 1}</td>
                      <td className="py-1.5 pr-2"><input value={d.type} onChange={e => setDefect(d.id, "type", e.target.value)} disabled={posted} className={`${txnInputCls} h-8`} placeholder="e.g. Shade variation" /></td>
                      <td className="py-1.5 pr-2"><input value={d.description} onChange={e => setDefect(d.id, "description", e.target.value)} disabled={posted} className={`${txnInputCls} h-8`} placeholder="Description" /></td>
                      <td className="py-1.5 pr-2"><NumericField value={d.qty} onChange={e => setDefect(d.id, "qty", e.target.value)} disabled={posted} className="h-8" /></td>
                      <td className="py-1.5 pr-2"><input value={d.remark} onChange={e => setDefect(d.id, "remark", e.target.value)} disabled={posted} className={`${txnInputCls} h-8`} placeholder="Remark" /></td>
                      <td className="py-1.5"><button onClick={() => rmDefect(d.id)} disabled={posted} className="text-[var(--fg-3)] hover:text-rose-400 transition" data-testid={`${TID}-defect-remove-${i}`}><TrashIcon size={14} weight="bold" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TransactionSection>

        {/* 5 · Remarks */}
        <TransactionSection index={5} title="Remarks" testId={`${TID}-sec-remarks`}>
          <FormField label="Operator Remarks" testId={`${TID}-remarks-field`}>
            <textarea data-testid={`${TID}-remarks`} value={remarks} onChange={e => setRemarks(e.target.value)} disabled={posted}
              className={`${txnTextareaCls} ${posted ? "opacity-70 cursor-not-allowed" : ""}`}
              placeholder="Optional — disposition or quality notes for this entry" />
          </FormField>
        </TransactionSection>

        {/* 6 · Summary */}
        <TransactionSection index={6} title="Summary" testId={`${TID}-sec-summary`}>
          <div className="max-w-xl">
            <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-2 px-3 pb-1 overline text-[var(--fg-3)]">
              <span>Quantity</span><span className="text-right">kg</span><span className="text-right">m</span>
            </div>
            <SummaryRow label="Available in PACK-WIP" kg={fmt(availKg)} m={fmM(availKg)} />
            <SummaryRow label="Total Checked" kg={fmt(totalN)} m={fmM(totalN)} />
            <SummaryRow label="First Quality" kg={fmt(firstN)} m={fmM(firstN)} tone="text-emerald-400" />
            <SummaryRow label="Seconds" kg={fmt(secondN)} m={fmM(secondN)} tone="text-amber-400" />
            <SummaryRow label="Waste" kg={fmt(wasteN)} m={fmM(wasteN)} tone="text-rose-400" />
            <div className="mt-1 rounded-sm bg-[var(--bg-2)] border border-[var(--border)]" data-testid={`${TID}-remaining`}>
              <SummaryRow label="Remaining Unchecked" kg={fmt(remaining)} m={fmM(remaining)} strong tone="text-[var(--accent)]" />
            </div>
          </div>
        </TransactionSection>
      </div>

      {/* ── Sticky Action Bar (REV-C §18/§20) ─────────────────── */}
      <TransactionActionBar testId={`${TID}-actionbar`}
        left={<button onClick={() => navigate(REGISTER_ROUTE)} data-testid={`${TID}-back`} className={`${txnBtn.ghost}`}><ArrowLeftIcon size={15} weight="bold" /> Back to Register</button>}>
        <button onClick={() => navigate(REGISTER_ROUTE)} data-testid={`${TID}-cancel`} className={`${txnBtn.ghost} ${txnBtnSize.standard}`}><XIcon size={14} weight="bold" /> Cancel</button>
        <button onClick={saveDraft} disabled={!canEdit || busy || posted || !conserved || !stockOk}
          data-testid={`${TID}-save`} className={`${txnBtn.secondary} ${txnBtnSize.standard}`}>
          <FloppyDiskIcon size={15} weight="bold" /> {draftId ? "Update Draft" : "Save Draft"}
        </button>
        <button onClick={() => setReviewOpen(true)} disabled={!canReview}
          title={!draftId ? "Save the draft first" : (pv && !pv.postable ? "Preconditions not met" : "")}
          data-testid={`${TID}-review-post`} className={`${txnBtn.primary} ${txnBtnSize.wide}`}>
          <CheckCircleIcon size={15} weight="bold" /> Review &amp; Post
        </button>
      </TransactionActionBar>

      {/* ── Review & Post Dialog (REV-C §22) ──────────────────── */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-md" data-testid={`${TID}-review-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Checking Post Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            {/* identity */}
            <div className="space-y-0.5">
              {[
                ["Document", draftNo || "—", true],
                ["Work Order", wo ? (wo.po_no || wo.wo_no) : "—", true],
                ["Product", product, true],
                ["Source", selSource ? (selSource.godown_code || "PACK-WIP") : "PACK-WIP", false],
                ["Source Batch", srcBatch, true],
              ].map(([k, v, mono]) => (
                <div key={k} className="flex items-center justify-between py-1 border-b border-[var(--border)]/60">
                  <span className="text-xs text-[var(--fg-2)]">{k}</span>
                  <span className={`${mono ? "font-mono" : ""} text-[var(--fg-0)] text-xs truncate max-w-[60%] text-right`}>{v}</span>
                </div>
              ))}
            </div>
            {/* REV-A · quantities with KG and METRES as SEPARATE columns */}
            <div className="rounded-sm border border-[var(--border)] overflow-hidden" data-testid={`${TID}-review-qty`}>
              <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-2 px-3 py-1.5 bg-[var(--bg-2)] overline text-[var(--fg-3)]">
                <span>Quantity</span><span className="text-right">kg</span><span className="text-right">m</span>
              </div>
              {[
                ["Available Before", availKg, "text-[var(--fg-0)]"],
                ["Total Checked", totalN, "text-[var(--fg-0)]"],
                ["First Quality", firstN, "text-emerald-400"],
                ["Seconds", secondN, "text-amber-400"],
                ["Waste", wasteN, "text-rose-400"],
                ["Remaining Unchecked", remaining, "text-[var(--accent)]"],
              ].map(([k, val, tone]) => (
                <div key={k} className="grid grid-cols-[1.4fr_1fr_1fr] gap-2 px-3 py-1 border-t border-[var(--border)]/60">
                  <span className="text-xs text-[var(--fg-2)]">{k}</span>
                  <span className={`font-mono text-xs text-right ${tone}`}>{fmt(val)}</span>
                  <span className={`font-mono text-xs text-right ${tone}`}>{fmM(val)}</span>
                </div>
              ))}
            </div>
            {/* BOM + mass balance */}
            <div className="flex items-center justify-between py-1 border-b border-[var(--border)]/60">
              <span className="text-xs text-[var(--fg-2)]">BOM Standard g/m</span>
              <span className="font-mono text-[var(--fg-0)] text-xs">{gpm ? `${fmt(gpm, 3)} g/m` : "—"}</span>
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs text-[var(--fg-2)]">Mass Balance</span>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400">
                <CheckCircleIcon size={14} weight="fill" /> Balanced
              </span>
            </div>
          </div>
          {!draftId && (
            <div className="mt-1 rounded-sm bg-amber-500/10 border border-amber-500/40 px-3 py-2 text-[11px] text-amber-400" data-testid={`${TID}-review-savehint`}>
              Save the draft first — then Confirm Post commits via the sealed Checking Stock Engine.
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <button onClick={() => setReviewOpen(false)} data-testid={`${TID}-review-cancel`} className={`${txnBtn.secondary} ${txnBtnSize.standard}`}>Cancel</button>
            <button onClick={postChecking} disabled={busy || !draftId || !(pv && pv.postable)} data-testid={`${TID}-confirm-post`} className={`${txnBtn.primary} ${txnBtnSize.medium}`}>
              <CheckCircleIcon size={15} weight="bold" /> Confirm Post
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
